'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import useSWRSubscription from 'swr/subscription';
import useSWRMutation from 'swr/mutation';
import { useI18n, useCurrentLocale } from '@locales/client';
import Cookies from 'js-cookie';
import { format, isToday, isYesterday, isThisYear } from 'date-fns';

import { useToast } from '@/shared/providers/toast-provider/ToastProvider';
import { fetcherWithToken } from '@/shared/api/fetcher';
import { getDateLocale } from '@/shared/libs/locale';
import { usePageVisitData } from '@/shared/hooks';

import { timestampToDate } from '@/features/wallet-managment/model/utils/groupTransactionsByDate';

export const useNotify = (user) => {
  const [notifications, setNotifications] = useState({
    balance: null,
    transaction: null,
    modal: null,
  });

  const [retryCount, setRetryCount] = useState(0);
  const socketRef = useRef(null);
  const { getPattern } = usePageVisitData();

  const retryTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);

  const { data, error } = useSWRSubscription(
    user.isAuth
      ? process.env.NEXT_PUBLIC_APP_ENV === 'development'
        ? `${process.env.NEXT_PUBLIC_WEB_SOCKET_URL}/notify`
        : `wss://${window.location.host}/server/notify`
      : null,
    (key, { next }) => {
      if (!key) return;

      const connectWebSocket = () => {
        if (isConnectingRef.current) return;

        isConnectingRef.current = true;
        const socket = new WebSocket(key);
        socketRef.current = socket;

        socket.addEventListener('open', handleOpen);
        socket.addEventListener('message', handleMessage);
        socket.addEventListener('error', handleError);
        socket.addEventListener('close', handleClose);

        function handleOpen() {
          const pattern = getPattern();
          socket.send(pattern);
          setRetryCount(0);
          isConnectingRef.current = false;
        }

        function handleMessage(event) {
          const messageData = JSON.parse(event.data);
          setNotifications((prev) => ({
            ...prev,
            ...messageData,
          }));
          next(null, event.data);
        }

        function handleError(event) {
          isConnectingRef.current = false;
          next(event.error);
          attemptReconnect();
        }

        function handleClose() {
          isConnectingRef.current = false;
          attemptReconnect();
        }

        return () => {
          socket.removeEventListener('open', handleOpen);
          socket.removeEventListener('message', handleMessage);
          socket.removeEventListener('error', handleError);
          socket.removeEventListener('close', handleClose);
          socket.close();
        };
      };

      const attemptReconnect = () => {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        setRetryCount((prev) => prev + 1);
        const delay = Math.min(1000 * 2 ** retryCount, 30000);
        retryTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      };

      connectWebSocket();

      return () => {
        clearTimeout(retryTimeoutRef.current);
        if (socketRef.current) {
          socketRef.current.close();
        }
      };
    }
  );

  const { showToast } = useToast();
  const t = useI18n();

  useEffect(() => {
    if (data) {
      try {
        const parsedData = JSON.parse(data);
        if (Array.isArray(parsedData)) {
          const [type, value] = parsedData;

          setNotifications((prev) => ({
            ...prev,
            [type]: value,
          }));

          // if (type === 'bonus' && value.dateFrom && value.dateTo) {
          //   setNotifications((prev) => ({
          //     ...prev,
          //     bonusDate: {
          //       dateFrom: value.dateFrom,
          //       dateTo: value.dateTo,
          //     },
          //   }));
          // }

          if (
            type === 'transaction' &&
            value.type === 'deposit' &&
            value.status === 'success'
          ) {
            // showToast({
            //   message: `${t('wallet.toasts.deposit_success')} $${value.sum}`,
            //   type: 'success',
            // });
          }

          if (
            type === 'transaction' &&
            value.type === 'withdraw' &&
            value.status === 'success'
          ) {
            // showToast({
            //   message: `${t('wallet.toasts.withdraw_success')} $${value.amountCrypto}`,
            //   type: 'success',
            // });
          }
          if (type === 'notify') {
            showToast(value);
            if (value.save) {
              mutate('/notifications/list');
            }
            if (value.type === 'bonus') {
              mutate('/profile/getBonuses');
              Cookies.set('bonusRecieved', true, {
                expires: 365,
              });
            }
          }
        } else {
          console.info('Unexpected data format:', parsedData);
        }
      } catch (e) {
        console.error('Failed to parse message data:', e);
      }
    }
  }, [data]);

  const isNotifyLoading = !data && !error && user.isAuth;

  return {
    isNotifyLoading,
    setNotifications,
    notifications,
  };
};

export const useNotifyList = () => {
  const {
    data: notifyList,
    isLoading: isNotifyListLoading,
    error: notifyListError,
    mutate: notifyListMutate,
  } = useSWR('/notifications/list', fetcherWithToken);
  const { trigger } = useSWRMutation(
    '/notifications/markRead',
    fetcherWithToken
  );

  return {
    notifyList,
    markReadAllTrigger: trigger,
    notifyListMutate,
    isNotifyListLoading: isNotifyListLoading,
    notifyListError: notifyListError,
  };
};

export const useNotificationDate = (notifyList) => {
  const t = useI18n();
  const locale = useCurrentLocale();

  const formatGroupHeader = (date: Date) => {
    // if (isToday(date)) return t('today');
    // if (isYesterday(date)) return t('yesterday');

    if (isThisYear(date)) {
      return format(date, 'd MMMM', { locale: getDateLocale(locale) });
    }

    return format(date, 'd MMMM yyyy', { locale: getDateLocale(locale) });
  };

  const formatNotificationTime = (date: Date) => {
    return format(date, 'HH:mm', { locale: getDateLocale(locale) });
  };

  const formatFullDate = (date: Date) => {
    return format(date, 'd MMMM, HH:mm', { locale: getDateLocale(locale) });
  };

  // Группировка уведомлений по дате
  const groupedNotifications = useMemo(() => {
    if (!Array.isArray(notifyList)) return [];

    const groups: Record<string, { date: Date; items: any[] }> = {};

    notifyList.forEach((item) => {
      const date = timestampToDate(item.ts);
      const normalizedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      const groupKey = normalizedDate.toISOString();

      if (!groups[groupKey]) {
        groups[groupKey] = {
          date: normalizedDate,
          items: [],
        };
      }

      groups[groupKey].items.push(item);
    });

    // Сортируем группы по дате (новые сверху)
    return Object.values(groups).sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  }, [notifyList]);

  return {
    groupedNotifications,
    formatGroupHeader,
    formatNotificationTime,
    formatFullDate,
  };
};
